import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import {
    getServices,
    getPOCServices,
    getDeviceLiquidationServices,
} from '@/modules/Service/services'
import { ServiceStatus } from '@/modules/Service/types'
import {
    createServiceColumns,
    createPOCServiceColumns,
    createServiceFilters,
    SERVICE_FILTER_DEFAULTS,
    SERVICE_DEFAULT_SORT,
    ALL_SERVICE_STATUSES,
    POC_SERVICE_STATUSES,
    type ServiceFilters,
} from '@/modules/Service/utils/service-list-config'

// ============================================================================
// Types
// ============================================================================

interface UseServiceListOptions {
    /** Service type: 'all' | 'poc' | 'device-liquidation' */
    type?: 'all' | 'poc' | 'device-liquidation'
    /** Filter statuses to show */
    serviceStatuses?: ServiceStatus[]
    /** Show account manager filter */
    showAccountManager?: boolean
    /** Show date filters */
    showDateFilters?: boolean
    /** Show contract role/direction filters */
    showContractFilters?: boolean
}

// ============================================================================
// Hook: useServiceList
// ============================================================================

/**
 * Business logic hook for Service List
 * Manages data fetching, pagination, sorting, and filtering
 */
export const useServiceList = (options: UseServiceListOptions = {}) => {
    const {
        type = 'all',
        serviceStatuses = type === 'poc'
            ? POC_SERVICE_STATUSES
            : ALL_SERVICE_STATUSES,
        showAccountManager = true,
        showDateFilters = true,
        showContractFilters = type === 'all',
    } = options

    const { t } = useTranslation('services')

    // URL params management
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<ServiceFilters>({
        defaults: {
            sort_by: SERVICE_DEFAULT_SORT.sort_by,
            sort_order: SERVICE_DEFAULT_SORT.sort_order,
            page_size: 20,
        },
        filterDefaults: SERVICE_FILTER_DEFAULTS,
    })

    // Form for filter controls
    const { control } = useForm<ServiceFilters>({
        defaultValues: filters,
    })

    // Select API and query key based on type
    const getApiConfig = () => {
        switch (type) {
            case 'poc':
                return {
                    apiCall: getPOCServices,
                    queryKey: QUERY_KEYS.SERVICES_POC,
                }
            case 'device-liquidation':
                return {
                    apiCall: getDeviceLiquidationServices,
                    queryKey: QUERY_KEYS.SERVICES_DEVICE_LIQUIDATION,
                }
            default:
                return {
                    apiCall: getServices,
                    queryKey: QUERY_KEYS.SERVICES,
                }
        }
    }
    const { apiCall, queryKey } = getApiConfig()

    // Fetch services data
    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [queryKey, queryParams],
        queryFn: () => apiCall(queryParams),
        placeholderData: true,
    })

    // Memoized columns & filters
    const columns = useMemo(
        () =>
            type === 'poc'
                ? createPOCServiceColumns(t)
                : createServiceColumns(t),
        [t, type]
    )

    const filterItems = useMemo(
        () =>
            createServiceFilters(t, {
                serviceStatuses,
                showAccountManager,
                showDateFilters,
                showContractFilters,
            }),
        [
            t,
            serviceStatuses,
            showAccountManager,
            showDateFilters,
            showContractFilters,
        ]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        services: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,

        // Params & Filters
        params,
        queryParams,
        control,
        setFilter,
        searchInputProps,

        // Configs
        columns,
        filterItems,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,

        // Translation
        t,
    }
}

export default useServiceList
